import type { StepProps } from '@/components/CreateCampaignDialog/createCampaignType'
import CloseCropIcon from '@/components/Icons/CloseCropIcon'
import CropIcon from '@/components/Icons/CropIcon'
import buttonStyles from '@/styles/buttons.module.css'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import Cropper from 'react-easy-crop'
import UploadLogoErrorDialog, { validateImageFile } from '../UploadLogoErrorDialog/UploadLogoErrorDialog.tsx'
import styles from './UploadImageStep.module.css'

interface CropArea {
  x: number
  y: number
  width: number
  height: number
}

export const UploadImageStep = ({ data, updateData, errors }: StepProps) => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null)
  const [croppedImage, setCroppedImage] = useState<string | null>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [showCropper, setShowCropper] = useState(false)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(null)
  const [isHovering, setIsHovering] = useState(false)
  const [errorText, setErrorText] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (data.coverImageFile && !imageFile) {
      setImageFile(data.coverImageFile)

      if (data.coverImagePreview) {
        setUploadedImage(data.coverImagePreview)
        setCroppedImage(data.coverImagePreview)
      } else {
        const reader = new FileReader()
        reader.onloadend = () => {
          const result = reader.result as string
          setUploadedImage(result)
          setCroppedImage(result)
        }
        reader.readAsDataURL(data.coverImageFile)
      }
    }
  }, [data.coverImageFile, data.coverImagePreview, imageFile])

  const handleButtonClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validationError = validateImageFile(file)

    if (validationError) {
      updateData({ coverImageFile: null })
      setUploadedImage(null)
      setCroppedImage(null)
      setImageFile(null)
      setErrorText(validationError)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      return
    }

    setImageFile(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      const preview = reader.result as string
      setUploadedImage(preview)
      setShowCropper(false)
      setCroppedImage(preview)
      updateData({ coverImageFile: file, coverImagePreview: preview })
    }
    reader.readAsDataURL(file)
  }

  const onCropComplete = useCallback((_croppedArea: CropArea, croppedAreaPixels: CropArea) => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const createCroppedImage = async () => {
    if (!uploadedImage || !croppedAreaPixels) return null

    const image = new Image()
    image.src = uploadedImage

    await new Promise((resolve) => {
      image.onload = resolve
    })

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    if (!ctx) return null

    canvas.width = croppedAreaPixels.width
    canvas.height = croppedAreaPixels.height

    ctx.drawImage(
      image,
      croppedAreaPixels.x,
      croppedAreaPixels.y,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
      0,
      0,
      croppedAreaPixels.width,
      croppedAreaPixels.height,
    )

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob)
      }, 'image/jpeg')
    })
  }

  const handleCropSave = async () => {
    const croppedBlob = await createCroppedImage()
    if (croppedBlob) {
      const croppedFile = new File([croppedBlob], imageFile?.name || 'cropped-image.jpg', {
        type: 'image/jpeg',
      })
      setImageFile(croppedFile)

      const croppedImageUrl = URL.createObjectURL(croppedBlob)
      setCroppedImage(croppedImageUrl)
      setShowCropper(false)

      updateData({ coverImageFile: croppedFile, coverImagePreview: croppedImageUrl })
    }
  }

  const handleCropCancel = () => {
    setShowCropper(false)
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }

  const handleCropClick = () => {
    setShowCropper(true)
  }

  const handleDeleteImage = () => {
    setUploadedImage(null)
    setCroppedImage(null)
    setImageFile(null)
    setShowCropper(false)
    updateData({ coverImageFile: null, coverImagePreview: null })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  return (
    <div className={styles.Container}>
      <UploadLogoErrorDialog isOpen={!!errorText} onClose={() => setErrorText(null)} message={errorText || ''} />
      <div
        className={styles.UploadArea}
        style={croppedImage && !showCropper ? { backgroundImage: `url(${croppedImage})` } : {}}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {showCropper && uploadedImage ? (
          <div className={styles.CropperContainer}>
            <Cropper
              image={uploadedImage}
              crop={crop}
              zoom={zoom}
              aspect={16 / 9}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
        ) : !croppedImage ? (
          <>
            <div className={styles.IconWrapper}>
              <img src="/addPhoto.svg" alt="Upload" />
            </div>
            <p className={styles.Text}>Upload background image for your Campaign here</p>
            <div>
              <button type="button" className={buttonStyles['button-primary']} onClick={handleButtonClick} style={{ marginBottom: '24px' }}>
                Upload Image
              </button>
            </div>
          </>
        ) : null}

        {croppedImage && !showCropper && isHovering && (
          <div className={styles.HoverOverlay}>
            <div className={styles.HoverButtons}>
              <button type="button" className={styles.ButtonAction} onClick={handleDeleteImage}>
                <CloseCropIcon color="white" />
                Delete
              </button>
              <button type="button" className={styles.ButtonAction} onClick={handleCropClick}>
                <CropIcon color="white" />
                Crop
              </button>
            </div>
          </div>
        )}
      </div>

      {showCropper && (
        <div className={styles.CropControls}>
          <div className={styles.ZoomControl}>
            <label htmlFor="zoom">Zoom</label>
            <input type="range" id="zoom" min={1} max={3} step={0.1} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          </div>
          <div className={styles.ButtonGroup}>
            <div>
              <button type="button" className={buttonStyles['button-outline']} onClick={handleCropCancel}>
                Cancel
              </button>
            </div>
            <div>
              <button type="button" className={buttonStyles['button-primary']} onClick={handleCropSave}>
                Save Crop
              </button>
            </div>
          </div>
        </div>
      )}
      {errors?.coverImageFile && <div className={styles.Error}>{errors.coverImageFile}</div>}
      <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" className={styles.HiddenInput} />
    </div>
  )
}
